import { Link } from 'react-router-dom';
import {
  UXP_SC_ID,
  COMMAND_CENTER_METRICS,
  FORECAST_HORIZONS,
  FORECAST_INPUTS,
  REPLENISHMENT_FEATURES,
  PROCUREMENT_FEATURES,
  SUPPLIER_DIMENSIONS,
  MANUFACTURING_FEATURES,
  SHIPMENT_FEATURES,
  WAREHOUSE_FEATURES,
  FBA_METRICS,
  OPTIMIZATION_RECS,
  CASH_FLOW_METRICS,
  ADVISOR_CAPABILITIES,
  COLLABORATION_FEATURES,
  ANALYTICS_METRICS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_FORECASTS,
  DEMO_REPLENISHMENTS,
  DEMO_PURCHASE_ORDERS,
  DEMO_FBA,
  DEMO_WAREHOUSES,
  DEMO_OPTIMIZATIONS,
  DEMO_CASH_FLOW,
  buildSupplyChainDashboard,
} from '../../supplychain/framework';
import {
  InventoryCommandCenterPanel,
  DemandForecastPanel,
  ReplenishmentPlanningPanel,
  ProcurementPanel,
  FBACommandCenterPanel,
  WarehouseManagementPanel,
  InventoryOptimizationPanel,
  CashFlowIntelligencePanel,
} from '../../components/supplychain';
import { Card, KpiCard } from '../../components/ui';

export function SupplyChainPage() {
  const dashboard = buildSupplyChainDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_SC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Supply Chain Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Inventory Intelligence, FBA Operations, Supply Chain & Procurement Operating System —
          forecasts demand, optimizes inventory, orchestrates procurement, and coordinates FBA operations.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Forecast Horizons" value={dashboard.forecast_horizons.toString()} />
        <KpiCard label="FBA Metrics" value={dashboard.fba_metrics.toString()} />
        <KpiCard label="Optimization Recs" value={dashboard.optimization_recs.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Inventory Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <InventoryCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Demand Forecasting</h2>
        <p className="mt-1 text-sm text-slate-500">{FORECAST_INPUTS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DemandForecastPanel forecasts={DEMO_FORECASTS} horizons={FORECAST_HORIZONS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Replenishment Planning</h2>
          <p className="mt-1 text-sm text-slate-500">{REPLENISHMENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ReplenishmentPlanningPanel rows={DEMO_REPLENISHMENTS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Procurement</h2>
          <p className="mt-1 text-sm text-slate-500">{PROCUREMENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ProcurementPanel orders={DEMO_PURCHASE_ORDERS} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Supplier Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{SUPPLIER_DIMENSIONS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Shenzhen Audio Co.</span> — 96% on-time · 4.8 quality score · Low risk</p>
              <p><span className="font-medium">Guangzhou IoT Ltd.</span> — 88% on-time · 4.2 quality score · Medium risk</p>
              <p className="text-xs text-slate-500">Alternative: Dongguan Electronics — 12% cost savings, 14d lead time</p>
            </div>
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Manufacturing</h2>
          <p className="mt-1 text-sm text-slate-500">{MANUFACTURING_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">Production queue: 3 batches · 4,400 units</p>
              <p className="text-slate-600 dark:text-slate-400">Earbuds Pro batch — 72% complete · ETA Aug 8</p>
              <p className="text-xs text-warning-600">Bottleneck: Raw material delay on silicone tips (+3 days)</p>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Shipment Management</h2>
        <p className="mt-1 text-sm text-slate-500">{SHIPMENT_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="space-y-2 text-sm">
            <p><span className="font-medium">FBA-IN-2026-042</span> — 2,400 units to BLR8 · In transit · ETA Aug 4</p>
            <p><span className="font-medium">WH-XFER-2026-018</span> — 400 units Delhi → Bangalore · Scheduled Aug 2</p>
          </div>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Warehouse Management</h2>
          <p className="mt-1 text-sm text-slate-500">{WAREHOUSE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <WarehouseManagementPanel warehouses={DEMO_WAREHOUSES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">FBA Command Center</h2>
          <p className="mt-1 text-sm text-slate-500">{FBA_METRICS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <FBACommandCenterPanel snapshot={DEMO_FBA} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Inventory Optimization</h2>
        <p className="mt-1 text-sm text-slate-500">{OPTIMIZATION_RECS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <InventoryOptimizationPanel recommendations={DEMO_OPTIMIZATIONS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Cash Flow Intelligence</h2>
        <p className="mt-1 text-sm text-slate-500">{CASH_FLOW_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CashFlowIntelligencePanel rows={DEMO_CASH_FLOW} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Supply Chain Advisor</h2>
        <p className="mt-1 text-sm text-slate-500">{ADVISOR_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="space-y-2 text-sm">
            <p className="font-medium">Priority: Expedite Hub Gen 3 replenishment before Prime Day window</p>
            <p className="text-slate-600 dark:text-slate-400">
              Combined demand + ad velocity signals predict 6-day stockout risk. Recommended PO approval today.
            </p>
            <p className="text-xs text-brand-600">Expected business impact: +₹4.2L revenue protected · 91% confidence</p>
          </div>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{ANALYTICS_METRICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
